import React, { useState, useEffect } from 'react'
import { Project, User, ProjectFormData } from '../../types'
import Tooltip from '../common/Tooltip'

interface ProjectFormProps {
  project?: Project
  users: User[]
  onSubmit: (data: ProjectFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  users,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    color: '#f59e0b',
    budgetType: undefined,
    budgetAmount: undefined,
    memberIds: [],
    isActive: true
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // 預設顏色選項
  const colorOptions = [
    '#f59e0b', // 琥珀金
    '#3b82f6', // 藍色
    '#10b981', // 綠色
    '#f97316', // 橙色
    '#8b5cf6', // 紫色
    '#ef4444', // 紅色
    '#06b6d4', // 青色
    '#84cc16', // 萊姆綠
    '#f43f5e', // 玫瑰紅
    '#6b7280'  // 灰色
  ]

  // 初始化表單數據
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        color: project.color,
        budgetType: project.budget?.type?.toLowerCase() as 'monthly' | 'total' | undefined,
        budgetAmount: project.budget?.amount,
        showBudgetToMembers: project.budget?.showBudgetToMembers || false,
        memberIds: project.members?.map(m => m.userId) || [],
        isActive: project.isActive
      })
    }
  }, [project])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = '專案名稱為必填項目'
    }

    if (formData.budgetType && !formData.budgetAmount) {
      newErrors.budgetAmount = '請輸入預算金額'
    }

    if (formData.budgetAmount && formData.budgetAmount <= 0) {
      newErrors.budgetAmount = '預算金額必須大於 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleInputChange = (field: keyof ProjectFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // 清除相關錯誤
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleMemberToggle = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(userId)
        ? prev.memberIds.filter(id => id !== userId)
        : [...prev.memberIds, userId]
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* 表單標題 */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-secondary-800">
              {project ? '編輯專案' : '建立新專案'}
            </h2>
          </div>

          {/* 表單內容 */}
          <div className="px-6 py-4 space-y-6">
            {/* 專案名稱 */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                專案名稱 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="輸入專案名稱"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* 專案描述 */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                專案描述
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="輸入專案描述（選填）"
              />
            </div>

            {/* 專案顏色 */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                專案顏色
              </label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleInputChange('color', color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color
                        ? 'border-secondary-400 scale-110'
                        : 'border-gray-300 hover:border-secondary-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* 專案狀態 */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-3">
                專案狀態
              </label>
              <div className="space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="isActive"
                    checked={formData.isActive !== false}
                    onChange={() => handleInputChange('isActive', true)}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      <span className="font-medium text-secondary-800">進行中</span>
                    </div>
                    <div className="text-sm text-secondary-600 mt-1">
                      團隊成員可以在此專案記錄工時，專案會顯示在計時器選單中
                    </div>
                  </div>
                </label>
                
                <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="isActive"
                    checked={formData.isActive === false}
                    onChange={() => handleInputChange('isActive', false)}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      <span className="font-medium text-secondary-800">已停用</span>
                    </div>
                    <div className="text-sm text-secondary-600 mt-1">
                      專案暫停或完成，無法記錄新工時，但保留所有歷史資料
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* 預算設定 */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                預算設定
              </label>
              <div className="space-y-3">
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budgetType"
                      value="monthly"
                      checked={formData.budgetType === 'monthly'}
                      onChange={(e) => handleInputChange('budgetType', e.target.value)}
                      className="mr-2"
                    />
                    月度預算
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budgetType"
                      value="total"
                      checked={formData.budgetType === 'total'}
                      onChange={(e) => handleInputChange('budgetType', e.target.value)}
                      className="mr-2"
                    />
                    總預算
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budgetType"
                      value=""
                      checked={!formData.budgetType}
                      onChange={() => handleInputChange('budgetType', undefined)}
                      className="mr-2"
                    />
                    不設定預算
                  </label>
                </div>

                {formData.budgetType && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={formData.budgetAmount || ''}
                        onChange={(e) => handleInputChange('budgetAmount', parseFloat(e.target.value) || undefined)}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.budgetAmount ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="輸入預算金額"
                        min="0"
                        step="0.01"
                      />
                      <span className="text-secondary-600">TWD</span>
                    </div>
                    {errors.budgetAmount && (
                      <p className="mt-1 text-sm text-red-600">{errors.budgetAmount}</p>
                    )}
                    
                    {/* 對成員顯示預算選項 */}
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.showBudgetToMembers || false}
                          onChange={(e) => handleInputChange('showBudgetToMembers', e.target.checked)}
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-secondary-700">
                          對成員顯示預算
                        </span>
                      </label>
                      
                      <Tooltip
                        content={
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <span className="text-blue-400">💡</span>
                              <span>勾選後，專案成員可以查看預算使用情況和進度</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <span className="text-amber-400">⚠️</span>
                              <span>預算資訊包含敏感的財務數據</span>
                            </div>
                          </div>
                        }
                        position="top"
                      >
                        <button
                          type="button"
                          className="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 
                                     flex items-center justify-center text-gray-500 text-xs
                                     transition-colors cursor-help border border-gray-300"
                        >
                          ?
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 專案成員 */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                專案成員
              </label>
              <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto">
                {users.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.memberIds.includes(user.id)}
                      onChange={() => handleMemberToggle(user.id)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-secondary-800">
                        {user.name}
                      </div>
                      <div className="text-xs text-secondary-600">
                        {user.email}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                      user.role === 'MANAGER' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {user.role === 'ADMIN' ? '管理員' :
                       user.role === 'MANAGER' ? '主管' : '員工'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* 表單按鈕 */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500"
              disabled={isLoading}
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? '處理中...' : (project ? '更新專案' : '建立專案')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProjectForm
